// Dense aggregate tile — FE-SCRM-008.
//
// Renders when a cell has 4+ opportunities. Spec line 597: large
// count number, aggregate value estimate, horizontal distribution
// bar segmented by cadence_health proportions, worst-severity
// border. Click opens the cell modal.

import {
  CADENCE_BG_DOT, CADENCE_BORDER, CADENCE_DISTRIBUTION_ORDER,
  CADENCE_LABEL,
} from "./cadenceColors";
import type { CadenceHealth, RenewalBoardCell } from "./types";


interface Props {
  cell: RenewalBoardCell;
  monthKey: string; // "YYYY-MM"
  onClick: (cell: { month: string; stage: string }) => void;
}


function shortValue(cents: number | null): string {
  if (!cents) return "";
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${Math.round(dollars / 1_000)}K`;
  return `$${Math.round(dollars)}`;
}


function DistributionBar({
  distribution, total,
}: {
  distribution: Record<CadenceHealth, number>;
  total: number;
}) {
  if (total === 0) return null;
  return (
    <div className="flex w-full h-1.5 rounded overflow-hidden">
      {CADENCE_DISTRIBUTION_ORDER.map((h) => {
        const n = distribution[h] || 0;
        if (n === 0) return null;
        const pct = (n / total) * 100;
        return (
          <div
            key={h}
            className={CADENCE_BG_DOT[h]}
            style={{ width: `${pct}%` }}
            title={`${n} ${CADENCE_LABEL[h].toLowerCase()}`}
            data-cadence-segment={h}
          />
        );
      })}
    </div>
  );
}


export function CountTile({ cell, monthKey, onClick }: Props) {
  const border = cell.worst_cadence_health
    ? CADENCE_BORDER[cell.worst_cadence_health]
    : "border-l-4 border-border-subtle";
  const value = shortValue(cell.total_value_estimate);
  return (
    <button
      type="button"
      data-testid={`count-tile-${monthKey}-${cell.stage}`}
      data-cadence-health={cell.worst_cadence_health ?? "none"}
      onClick={() => onClick({ month: monthKey, stage: cell.stage })}
      className={[
        // No ``h-full``: the parent cell now stretches to fill the
        // grid row track (so the drop target spans the full
        // swimlane), and h-full would propagate that stretch onto
        // the tile itself, making a single dense cell tower over
        // the cards around it.
        "w-full text-left rounded-md bg-raised px-2 py-1.5",
        "cursor-pointer hover:bg-recessed flex flex-col gap-1",
        border,
      ].join(" ")}
      title={
        `${cell.count} opportunities${value ? ` · ${value}` : ""}`
      }
    >
      <div className="flex items-baseline gap-2">
        <span className="text-lg font-semibold text-text-primary tabular-nums">
          {cell.count}
        </span>
        {value && (
          <span className="text-xs text-text-tertiary tabular-nums">
            {value}
          </span>
        )}
      </div>
      <DistributionBar
        distribution={cell.cadence_health_distribution}
        total={cell.count}
      />
    </button>
  );
}
